import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MARKETING_HEAD_ATTR = 'data-audiograph-marketing-head';
const PAGE_FILES: Record<string, string> = {
  '/': 'index.html',
  '/contact': 'contact.html',
  '/contact.html': 'contact.html',
  '/gallery': 'gallery.html',
  '/gallery.html': 'gallery.html',
  '/services': 'services.html',
  '/services.html': 'services.html',
};

function rewriteAssetUrl(value: string): string {
  if (value.startsWith('/css/') || value.startsWith('/js/') || value.startsWith('/images/') || value === '/favicon.png') {
    return `/marketing-v5${value}`;
  }
  return value;
}

function rewriteElementAssets(element: Element) {
  for (const attribute of ['src', 'href', 'poster']) {
    const value = element.getAttribute(attribute);
    if (value) element.setAttribute(attribute, rewriteAssetUrl(value));
  }
  const style = element.getAttribute('style');
  if (style) element.setAttribute('style', style.replace(/url\((['"]?)\/(css|js|images)\//g, 'url($1/marketing-v5/$2/'));
}

export function PublicSite() {
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const pageFile = PAGE_FILES[location.pathname] ?? 'index.html';

  useEffect(() => {
    let cancelled = false;
    const addedHeadNodes: Node[] = [];
    const root = rootRef.current;
    const appRoot = root?.parentElement;
    const previousAppRootId = appRoot?.getAttribute('id');

    async function mountMarketingSite() {
      try {
        setError(null);
        const response = await fetch(`/marketing-v5/${pageFile}`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Marketing site load failed (${response.status})`);
        const html = await response.text();
        if (cancelled || !root) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        document.querySelectorAll(`[${MARKETING_HEAD_ATTR}]`).forEach((node) => node.remove());
        // The V5 bundle mounts into #root. Temporarily release the OS shell's
        // root id so the packaged marketing app gets the unique mount target.
        appRoot?.removeAttribute('id');

        if (parsed.title) {
          document.title = parsed.title;
          const title = document.createElement('title');
          title.setAttribute(MARKETING_HEAD_ATTR, 'true');
          title.textContent = parsed.title;
          document.head.appendChild(title);
          addedHeadNodes.push(title);
        }

        parsed.head.querySelectorAll('meta, link, style, script').forEach((source) => {
          rewriteElementAssets(source);
          const node = document.createElement(source.tagName.toLowerCase());
          for (const attribute of Array.from(source.attributes)) node.setAttribute(attribute.name, attribute.value);
          node.textContent = source.textContent;
          node.setAttribute(MARKETING_HEAD_ATTR, 'true');
          document.head.appendChild(node);
          addedHeadNodes.push(node);
        });

        root.innerHTML = parsed.body.innerHTML;
        root.querySelectorAll('[src], [href], [poster], [style]').forEach(rewriteElementAssets);
        root.querySelectorAll('script').forEach((source) => {
          const script = document.createElement('script');
          for (const attribute of Array.from(source.attributes)) script.setAttribute(attribute.name, rewriteAssetUrl(attribute.value));
          script.textContent = source.textContent;
          source.replaceWith(script);
        });
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Marketing site could not be loaded');
      }
    }

    void mountMarketingSite();
    return () => {
      cancelled = true;
      addedHeadNodes.forEach((node) => node.parentNode?.removeChild(node));
      if (root) root.innerHTML = '';
      if (appRoot && previousAppRootId !== null && previousAppRootId !== undefined) appRoot.setAttribute('id', previousAppRootId);
    };
  }, [pageFile]);

  if (error) return <main className="marketing-load-error" role="alert">{error}</main>;
  return <div ref={rootRef} className="marketing-site" aria-label="Audiograph public website" />;
}
